import React, { useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useAuthGate } from './authPrompt'
import * as normalActions from '../store/actions'
import { addToPlaylist, createPlaylist, getPlaylists, playlistHas, removeFromPlaylist } from '../utils/playlists'
import { shareMedia } from '../utils/mediaRoute'
import '../styles/compsStyles/playlistSave.css'

const PlaylistSave = ({ post, className, icon, saveOnly = false }) => {
  const dispatch = useDispatch()
  const userId = useSelector(state => state.authReduse.id)
  const { requireAuth, promptEl } = useAuthGate()
  const menuRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [view, setView] = useState('root')
  const [pos, setPos] = useState({ top: 0, left: 0, width: 260 })
  const [lists, setLists] = useState([])
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')

  const postSnap = {
    id: post?.id,
    title: post?.title,
    thumbnail: post?.thumbnail,
    type: post?.type || (post?.photo ? 'i' : 'l'),
    image: post?.image || post?.thumbnail || ''
  }
  const inList = (playlist) => playlistHas(playlist, postSnap.id)
  const empty = lists.length === 0

  const placeMenu = (event, width = 260, height = 220) => {
    const rect = (menuRef.current || event.currentTarget).getBoundingClientRect()
    const w = Math.min(width, window.innerWidth - 16)
    let left = rect.right - w
    if (left < 8) left = 8
    if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8
    let top = rect.bottom + 6
    if (top + height > window.innerHeight - 8) {
      top = Math.max(8, rect.top - height - 6)
    }
    setPos({ top, left, width: w })
  }

  const openMenu = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (!postSnap.id) return
    setNote('')
    setReason('')
    setName('')
    if (saveOnly) {
      if (!requireAuth()) return
      const current = getPlaylists(userId)
      placeMenu(event, 280, current.length ? Math.min(280, 44 + current.length * 44) : 150)
      setLists(current)
      setView('save')
    } else {
      placeMenu(event, 240, 168)
      setView('root')
    }
    setOpen(true)
  }

  const closeSave = (event) => {
    if (event) event.stopPropagation()
    setOpen(false)
    setView('root')
  }

  const openSave = (event) => {
    event.stopPropagation()
    if (!requireAuth()) return
    const current = getPlaylists(userId)
    placeMenu(event, 280, current.length ? Math.min(280, 80 + current.length * 44) : 170)
    setLists(current)
    setName('')
    setNote('')
    setView('save')
  }

  const sharePost = async (event) => {
    event.stopPropagation()
    const result = await shareMedia(postSnap)
    if (result === 'abort') return
    if (result === 'copied') {
      setNote('Link copied')
      setView('root')
      placeMenu(event, 240, 188)
      return
    }
    closeSave()
  }

  const openReport = (event) => {
    event.stopPropagation()
    if (!requireAuth()) return
    setReason('')
    setNote('')
    placeMenu(event, 280, 220)
    setView('report')
  }

  const sendReport = async (event) => {
    event.preventDefault()
    event.stopPropagation()
    const content = reason.trim() || 'Report'
    try {
      await dispatch(normalActions.add_to_report(postSnap.id, userId, content, ''))
      closeSave()
    } catch (err) {
      setNote(err.message || 'Could not send report')
    }
  }

  const toggleList = (event, playlist) => {
    event.stopPropagation()
    if (inList(playlist)) {
      setLists(removeFromPlaylist(userId, playlist.id, postSnap.id))
      setNote(`Removed from ${playlist.name}`)
    } else {
      setLists(addToPlaylist(userId, playlist.id, postSnap))
      setNote(`Saved to ${playlist.name}`)
    }
  }

  const makeList = (event) => {
    event.preventDefault()
    event.stopPropagation()
    const title = name.trim() || 'My playlist'
    const next = createPlaylist(userId, title)
    const created = next[0]
    const saved = addToPlaylist(userId, created.id, postSnap)
    setLists(saved)
    setName('')
    setNote(`Saved to ${created.name}`)
  }

  const count = (item) => (item.postIds || []).length

  return (
    <>
      {promptEl}
      <button
        type="button"
        ref={menuRef}
        className={className}
        aria-label={saveOnly ? 'Save to playlist' : 'More'}
        onClick={openMenu}
      >
        {icon}
      </button>
      {open && ReactDOM.createPortal(
        <div className="playlistMenu" onClick={closeSave} role="presentation">
          <div
            className="playlistMenuCard"
            style={{ top: pos.top, left: pos.left, width: pos.width }}
            onClick={event => event.stopPropagation()}
          >
            {view === 'root' ? (
              <>
                <p className="playlistMenuTitle">More</p>
                <ul className="playlistMenuList">
                  <li>
                    <button type="button" onClick={sharePost}>Share</button>
                  </li>
                  <li>
                    <button type="button" onClick={openReport}>Report</button>
                  </li>
                  <li>
                    <button type="button" onClick={openSave}>Save to playlist</button>
                  </li>
                </ul>
                {note ? <p className="playlistMenuNote">{note}</p> : null}
              </>
            ) : null}

            {view === 'save' ? (
              <>
                {!saveOnly ? (
                  <button type="button" className="playlistMenuBack" onClick={() => setView('root')}>Back</button>
                ) : null}
                <p className="playlistMenuTitle">Save to</p>
                {!empty ? (
                  <ul className="playlistMenuList">
                    {lists.map(item => (
                      <li key={item.id}>
                        <button type="button" onClick={event => toggleList(event, item)}>
                          <span className={`playlistMenuCheck${inList(item) ? ' isOn' : ''}`} />
                          <span className="playlistMenuMeta">
                            <strong>{item.name}</strong>
                            <em>{count(item)} {count(item) === 1 ? 'video' : 'videos'}</em>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <form className="playlistMenuCreate" onSubmit={makeList}>
                    <p className="playlistMenuHint">Create a playlist to save this video.</p>
                    <input
                      value={name}
                      onChange={event => setName(event.target.value)}
                      onClick={event => event.stopPropagation()}
                      placeholder="Playlist name"
                      maxLength={40}
                    />
                    <button type="submit">Create playlist</button>
                  </form>
                )}
                {note ? <p className="playlistMenuNote">{note}</p> : null}
              </>
            ) : null}

            {view === 'report' ? (
              <form className="playlistMenuCreate" onSubmit={sendReport}>
                <button type="button" className="playlistMenuBack" onClick={() => setView('root')}>Back</button>
                <p className="playlistMenuTitle">Report</p>
                <textarea
                  className="playlistMenuReason"
                  value={reason}
                  onChange={event => setReason(event.target.value)}
                  onClick={event => event.stopPropagation()}
                  placeholder="Why are you reporting this?"
                />
                <button type="submit">Send report</button>
                {note ? <p className="playlistMenuNote">{note}</p> : null}
              </form>
            ) : null}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default PlaylistSave
